/**
 * Inserts markdown syntax rather than editing rich text, so the stored document stays
 * byte-identical to what was typed. Deliberately does not use the components that
 * round-trip through markdown-draft-js since this is specific for legal text.
 */
import React from 'react';

import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import LinkIcon from '@mui/icons-material/Link';
import { Button, Divider, IconButton, Stack, Tooltip } from '@mui/material';

export type MarkdownTransform = (
  value: string,
  selectionStart: number,
  selectionEnd: number,
) => { value: string; selectionStart: number; selectionEnd: number };

const trimSelection = (value: string, start: number, end: number): [number, number] => {
  let trimmedStart = start;
  let trimmedEnd = end;

  while (trimmedStart < trimmedEnd && /\s/.test(value[trimmedStart])) {
    trimmedStart += 1;
  }
  while (trimmedEnd > trimmedStart && /\s/.test(value[trimmedEnd - 1])) {
    trimmedEnd -= 1;
  }

  return [trimmedStart, trimmedEnd];
};

const wrapSelection =
  (marker: string): MarkdownTransform =>
  (value, start, end) => {
    const [from, to] = trimSelection(value, start, end);
    const selected = value.slice(from, to);

    return {
      value: `${value.slice(0, from)}${marker}${selected}${marker}${value.slice(to)}`,
      selectionStart: from + marker.length,
      selectionEnd: from + marker.length + selected.length,
    };
  };

const prefixLines =
  (prefix: string): MarkdownTransform =>
  (value, start, end) => {
    const blockStart = value.lastIndexOf('\n', start - 1) + 1;
    const nextNewline = value.indexOf('\n', end);
    const blockEnd = nextNewline === -1 ? value.length : nextNewline;

    const prefixed = value
      .slice(blockStart, blockEnd)
      .split('\n')
      .map((line) => (line.startsWith(prefix) ? line : `${prefix}${line}`))
      .join('\n');

    return {
      value: `${value.slice(0, blockStart)}${prefixed}${value.slice(blockEnd)}`,
      selectionStart: blockStart,
      selectionEnd: blockStart + prefixed.length,
    };
  };

const insertLink: MarkdownTransform = (value, start, end) => {
  const [from, to] = trimSelection(value, start, end);
  const selected = value.slice(from, to) || 'text';
  const urlStart = from + selected.length + 3;

  return {
    value: `${value.slice(0, from)}[${selected}](url)${value.slice(to)}`,
    selectionStart: urlStart,
    selectionEnd: urlStart + 'url'.length,
  };
};

interface Props {
  onApply: (transform: MarkdownTransform) => void;
  disabled?: boolean;
}

export default function MarkdownToolbar({ onApply, disabled = false }: Props) {
  const iconActions = [
    { label: 'Bold', icon: <FormatBoldIcon fontSize="small" />, transform: wrapSelection('**') },
    { label: 'Italic', icon: <FormatItalicIcon fontSize="small" />, transform: wrapSelection('*') },
    { label: 'Bulleted list', icon: <FormatListBulletedIcon fontSize="small" />, transform: prefixLines('- ') },
    { label: 'Numbered list', icon: <FormatListNumberedIcon fontSize="small" />, transform: prefixLines('1. ') },
    { label: 'Link', icon: <LinkIcon fontSize="small" />, transform: insertLink },
  ];

  return (
    <Stack direction="row" alignItems="center" spacing={0.25}>
      {(['##', '###'] as const).map((hashes) => (
        <Tooltip key={hashes} title={`Heading ${hashes.length}`}>
          <span>
            <Button
              size="small"
              color="inherit"
              disabled={disabled}
              onClick={() => onApply(prefixLines(`${hashes} `))}
              sx={{ minWidth: 32, px: 0.5, fontWeight: 600 }}
            >
              H{hashes.length}
            </Button>
          </span>
        </Tooltip>
      ))}

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />

      {iconActions.map((action) => (
        <Tooltip key={action.label} title={action.label}>
          <span>
            <IconButton size="small" disabled={disabled} onClick={() => onApply(action.transform)}>
              {action.icon}
            </IconButton>
          </span>
        </Tooltip>
      ))}
    </Stack>
  );
}
